import React, { useEffect, useState } from 'react';
import ContratLocationForm from '../components/Forms/ContratLocationForm';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContratLocationData, setSelectedContratLocation } from '../reduxStore/contratLocationSlice';
import { ContratLocation } from '../models/models';
import { TableAction, TableHeader } from '../constants/interfaces';
import { ButtonType } from '../constants/enums';
import MyTable from '../components/MyTable';
import { AppDispatch } from '../reduxStore/Store';
import { Checkbox, DialogTitle, Typography } from '@mui/material';
import { formatStringDate } from '../utils/utils';
import { labels } from '../constants/labels';
import { useDialog } from '../components/DialogComponent';
import ArticleIcon from '@mui/icons-material/Article';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';

const GestionContratLocation: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { contratLocationData, total, loading, successMessage } = useSelector((state: any) => state.ContratLocationSlice);
    const selectedContrat = useSelector((state: any) => state.ContratLocationSlice.selectedContratLocation);
    const [fetchParams, setFetchParams] = useState({ limit: 5, offset: 0 });

    const [FormDialog, openDialog, closeDialog] = useDialog({
        dialogProps: { maxWidth: 'md', fullWidth: true }
    });

    const handleCancel = () => {
        closeDialog();
        dispatch(setSelectedContratLocation(null));
    };

    useEffect(() => {
        dispatch(fetchContratLocationData(fetchParams));
    }, [dispatch, fetchParams, successMessage]);

    const headers: TableHeader<ContratLocation>[] = [
        { key: "numero_contrat", label: "Numero Contrat" },
        { key: "date_debut", label: "Date Debut", render: (row) => formatStringDate(row.date_debut) },
        { key: "date_fin", label: "Date Fin", render: (row) => formatStringDate(row.date_fin) },
        { key: "loyer", label: "Loyer" },
        { key: "depot_garantie", label: "Depot Garantie" },
        { key: "revision_loyer", label: "Revision Loyer",
            render: (row) => <Checkbox checked={row.revision_loyer} disabled color="primary" />
        },
        { key: "enregistre", label: "Contrat enregistre",
            render: (row) => <Checkbox checked={row.enregistre} disabled color="primary" />
        },
        { key: "etat", label: "Etat" },
        { key: "locataire", label: labels.client,
            render: (row) => row.locataire ? `${row.locataire.nom} ${row.locataire.prenom}` : "N/A"
        },
        { key: "bien", label: "Bien",
            render: (row) => row.bien
                ? `${row.bien.nom} - ${row.bien?.groupe_bien?.nom ?? "N/A"}`
                : "N/A"
        },
    ];

    const tableActions: TableAction<ContratLocation>[] = [
        {
            label: "Edit",
            callback: (cl) => {
                const updatedCl = {
                    ...cl,
                    date_debut: cl.date_debut.split("T")[0],
                    date_fin: cl.date_fin.split("T")[0],
                };
                dispatch(setSelectedContratLocation(updatedCl));
                openDialog();
            },
            type: ButtonType.EDIT,
        },
    ];

    const handlePageChange = (newPage: number, newRowsPerPage: number) => {
        setFetchParams({
            ...fetchParams,
            offset: newPage * newRowsPerPage,
            limit: newRowsPerPage,
        });
    };

    return (
        <AppPage maxWidth={false}>
            <AppPageHeader
                title={labels.gestionContrat}
                subtitle={`${total} contrat${total > 1 ? 's' : ''}`}
                icon={<ArticleIcon />}
                addLabel={`Ajouter un ${labels.contrat}`}
                onAdd={openDialog}
            />

            {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}

            <AppSurface>
                <MyTable
                    headers={headers}
                    data={contratLocationData}
                    onPageChange={handlePageChange}
                    actions={tableActions}
                    totalRows={total}
                />
            </AppSurface>

            <FormDialog>
                <DialogTitle>
                    <Typography variant="h6">
                        {selectedContrat ? `Modifier un ${labels.contrat}` : `Ajouter un ${labels.contrat}`}
                    </Typography>
                </DialogTitle>
                <ContratLocationForm onCancel={handleCancel} />
            </FormDialog>
        </AppPage>
    );
};

export default GestionContratLocation;
